import {
  formatColumnLabelWithKind,
  formatSnakeCase,
  KIND_TYPE,
  mixColumnNameWithKind
} from '@API/helpers/v2_helpers';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import toLower from 'lodash/toLower';
import { formatNameForDisplay } from '@shared/utils/displayHelpers';

interface SelectOption<T = string | number> {
  value: T;
  label: string;
  source?: string;
  secondary?: string;
  extraOptions?: SelectOption[];
  kind?: string;
  [key: string]: unknown;
}

interface Column {
  id: string;
  name: string;
  source: string;
  kinds?: string[];
}

interface GroupedOption<T = unknown> {
  label: string;
  options: T[];
}

interface ExpandableGroupedOption extends GroupedOption {
  isExpanded: boolean;
}

interface ExtraOption {
  kind: string;
  label: string;
  source: string;
  value: string;
}

interface MetricItem {
  label: string;
  secondary?: string;
  source: string;
  value: string;
  extraOptions?: ExtraOption[];
}

type NameMap = Record<string, string> | undefined;

function getMetricsGroup(item: MetricItem): string {
  if (item.source !== 'metrics') {
    return item.source;
  }

  const isSystemMetric =
    (item.value?.startsWith('sys.') ?? false) ||
    (item.label?.toLowerCase().includes('system') ?? false);

  return isSystemMetric ? 'system_metrics' : 'metrics';
}

export function addExpandedField(
  items: GroupedOption[],
  shouldExpand: (item: GroupedOption) => boolean = () => true
): ExpandableGroupedOption[] {
  return items.map(item => ({
    ...item,
    isExpanded: shouldExpand(item)
  }));
}

export function formatValueForSelectOption<T>(
  value: T,
  source?: string,
  excludedValuesToFormat: T[] = [],
  extraOptions: SelectOption[] = [],
  option: Partial<SelectOption<T>> = {},
  nameMap?: NameMap
): SelectOption<T> {
  const label = excludedValuesToFormat?.includes(value)
    ? value
    : formatNameForDisplay(value, nameMap);

  return {
    ...option,
    value,
    label,
    source,
    secondary: source,
    extraOptions
  };
}

export function getGroupedOptionList<T extends Record<string, unknown>>(
  list: T[],
  groupedBy: keyof T
): GroupedOption<T>[] {
  const groupedListMap = list.reduce<Record<string, T[]>>((acc, item) => {
    const key = String(item[groupedBy]);
    (acc[key] || (acc[key] = [])).push(item);
    return acc;
  }, {});

  return Object.keys(groupedListMap).map(key => ({
    label: formatSnakeCase(key),
    options: groupedListMap[key],
    isExpanded: false
  }));
}

export function getGroupedOptionsBySourceSplittingMetrics(
  list: MetricItem[]
): GroupedOption[] {
  const listWithGroupKey = list.map(item => ({
    ...item,
    groupKey: getMetricsGroup(item)
  }));

  return getGroupedOptionList(listWithGroupKey, 'groupKey');
}

export function formatArrayAsSelectOptions<T = unknown>(
  values: T[]
): SelectOption<T>[] {
  return values.map(value => formatValueForSelectOption(value));
}

export function orderColumnsInAscOrder(columns: Column[]): Column[] {
  return orderBy(
    uniqBy(columns, ({ id }) => id),
    ({ id, name }) => `${toLower(formatNameForDisplay(name))}${id}`,
    'asc'
  );
}

export function minMaxColumnToOption(
  column: Column,
  rest: Partial<SelectOption> = {},
  nameMap?: NameMap
): SelectOption[] {
  return (column.kinds || [])
    .filter(kind => [KIND_TYPE.MAX, KIND_TYPE.MIN].includes(kind))
    .map(kind => ({
      ...rest,
      kind,
      value: mixColumnNameWithKind(column.name, kind),
      source: column.source,
      label: formatColumnLabelWithKind(
        formatNameForDisplay(column.name, nameMap),
        kind
      )
    }));
}

export function formatColumnsToSelectOptions(
  columns: Column[],
  nameMap?: NameMap
): SelectOption<string>[] {
  return columns.map(column => {
    const subOptions = minMaxColumnToOption(column, {}, nameMap);

    return formatValueForSelectOption(
      column.name,
      column.source,
      [],
      subOptions,
      {},
      nameMap
    );
  });
}
